import { useState } from 'react';
import { __ } from '@wordpress/i18n';

export default function ChildThemeWarningModal() {

  const [closed, setClosed]             = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  if (closed) return null;

  const dismissNotice = async () => {
    setIsDismissing(true);
    setErrorMessage('');
    try {
      const response = await fetch(
        responsive_addons_site_builder.ajax_url,
        {
          method: "POST",
          body: new URLSearchParams({
            action: 'responsive_addons_dismiss_sb_childtheme_warning',
            nonce: responsive_addons_site_builder.dismiss_nonce
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`status: ${response.status} => ${response.statusText}`);
      }

      setClosed(true);
    } catch (err) {
      setErrorMessage(__('Failed to dismiss the notice. Please check your connection and try again.', 'responsive-addons'));
      console.log('failed to dismiss the notice: ', err);
    } finally {
      setIsDismissing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white border rounded-2xl text-black p-7 w-full lg:max-w-3xl max-w-2xl shadow-xl relative">
        <div className="flex items-start gap-4">
          <div className="mt-1">
            <svg
              className="w-6 h-6 text-yellow-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5.07 19h13.86a1 1 0 00.86-1.5L13.41 4.5a1 1 0 00-1.72 0L4.21 17.5a1 1 0 00.86 1.5z" />
            </svg>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-4">
              {__('Child Theme Detected: Template Overrides May Affect Builder Features', 'responsive-addons')}
            </h2>

            <p className="text-sm leading-relaxed">
              {__('It looks like you\'re using a child theme and if you’ve overridden core templates like', 'responsive-addons')}{' '}
              <code className="px-1 bg-yellow-100 rounded text-yellow-900 mx-1">header.php</code>,
              <code className="px-1 bg-yellow-100 rounded text-yellow-900 mx-1">footer.php</code>,{' '}
              {__('or', 'responsive-addons')}{' '}
              <code className="px-1 bg-yellow-100 rounded text-yellow-900 mx-1">404.php</code>,
              {' '}{__('this may prevent some Site Builder features from working as expected.', 'responsive-addons')}
            </p>

            <p className="mt-4 text-sm">
              {__('For the best experience, please update your child theme’s templates.', 'responsive-addons')}{' '}
              {__('You can compare your child theme’s', 'responsive-addons')}{' '}
              <code className="px-1 bg-yellow-100 rounded text-yellow-900 mx-1">header.php</code>,
              <code className="px-1 bg-yellow-100 rounded text-yellow-900 mx-1">footer.php</code>,{' '}
              {__('and', 'responsive-addons')}{' '}
              <code className="px-1 bg-yellow-100 rounded text-yellow-900 mx-1">404.php</code>{' '}
              {__('files with the latest Responsive theme version.', 'responsive-addons')}
            </p>

            <p className="text-sm italic text-yellow-700 mt-4">
              {__('If your child theme doesn’t override any of the above templates, you can safely ignore this message.', 'responsive-addons')}
            </p>

            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                onClick={() => setClosed(true)}
                className="px-4 py-2 text-sm font-medium bg-btnPrimary text-white rounded-md"
              >
                {__('OK', 'responsive-addons')}
              </button>
              <button
                onClick={dismissNotice}
                className={`px-4 py-2 text-sm font-medium bg-btnPrimary text-white rounded-md ${isDismissing ? 'cursor-not-allowed opacity-75' : ''}`}
                disabled={isDismissing}
              >
                {isDismissing ? __('Dismissing', 'responsive-addons') : __('Dismiss', 'responsive-addons')}
              </button>
            </div>

            {errorMessage && (
              <p className="mt-2 text-sm text-red-600">
                {errorMessage}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}